import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaFacebookF, FaTwitter, FaLinkedinIn, FaPaperPlane, FaInstagram, FaYoutube, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import './Contact.css';

// Import components after all other imports
import CompassIcon from '../components/CompassIcon';
import GoogleReviews from '../components/GoogleReviews';
import SocialMediaFeeds from '../components/SocialMediaFeeds';

const Contact = () => {
  usePageTitle('Contact Us');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  
  const [formSubmitted, setFormSubmitted] = useState(false);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send the form data to your backend or email service
    console.log('Form submitted:', formData);
    setFormSubmitted(true);
    // Reset form after submission
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
    // Show success message for 5 seconds
    setTimeout(() => {
      setFormSubmitted(false);
    }, 5000);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <motion.div 
            className="contact-hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>Contact Us</h1>
            <p>Get in touch with our team for any inquiries or to request a quote</p>
          </motion.div>
        </div>
      </section>
      
      {/* Contact Information */}
      <section className="contact-info-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>Get In Touch</h2>
            <p>We'd love to hear from you. Here's how you can reach us.</p>
          </motion.div>
          
          <div className="contact-info-grid">
            <motion.div 
              className="contact-info-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="icon">
                <FaMapMarkerAlt />
              </div>
              <h3>Our Locations</h3>
              <div className="location-info">
                <div className="location">
                  <h4>Tanzania Office:</h4>
                  <p>Nelson Mandela Rd, Arusha, Tanzania</p>
                </div>
                <div className="location">
                  <h4>Kenya Office:</h4>
                  <p>Nairobi, Kenya</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              className="contact-info-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="icon">
                <FaPhone />
              </div>
              <h3>Phone Numbers</h3>
              <div className="phone-info">
                <div className="phone">
                  <h4>Tanzania:</h4>
                  <p>+255 765 603 466</p>
                </div>
                <div className="phone">
                  <h4>Kenya:</h4>
                  <p>+254 110 279 065</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              className="contact-info-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="icon">
                <FaEnvelope />
              </div>
              <h3>Email Addresses</h3>
              <div className="email-info">
                <div className="email">
                  <h4>Tanzania:</h4>
                  <p>info@houseofcanvas.net</p>
                </div>
                <div className="email">
                  <h4>Kenya:</h4>
                  <p>infoke@houseofcanvas.net</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              className="contact-info-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="icon">
                <FaClock />
              </div>
              <h3>Working Hours</h3>
              <div className="hours-info">
                <p>Monday - Friday: 8:00 AM - 6:00 PM</p>
                <p>Saturday: 9:00 AM - 4:00 PM</p>
                <p>Sunday: Closed</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Contact Form and Map */}
      <section className="contact-form-section">
        <div className="container">
          <div className="contact-grid">
            <motion.div 
              className="form-container"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2>Send Us a Message</h2>
              <p>Fill out the form below and we'll get back to you as soon as possible.</p>
              
              {formSubmitted && (
                <div className="form-success-message">
                  <p>Thank you for your message! We'll get back to you shortly.</p>
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Your Name</label>
                    <input 
                      type="text" 
                      id="name" 
                      name="name" 
                      value={formData.name}
                      onChange={handleChange}
                      required 
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <input 
                      type="email" 
                      id="email" 
                      name="email" 
                      value={formData.email}
                      onChange={handleChange}
                      required 
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input 
                      type="tel" 
                      id="phone" 
                      name="phone" 
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="subject">Subject</label>
                    <input 
                      type="text" 
                      id="subject" 
                      name="subject" 
                      value={formData.subject}
                      onChange={handleChange}
                      required 
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="message">Your Message</label>
                  <textarea 
                    id="message" 
                    name="message" 
                    rows="6" 
                    value={formData.message}
                    onChange={handleChange}
                    required
                  ></textarea>
                </div>
                <button type="submit" className="submit-button">
                  <FaPaperPlane style={{ marginRight: '8px' }} /> Send Message
                </button>
              </form>
              
              
            </motion.div>
            
            <motion.div 
              className="map-container"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2>Find Us On Map</h2>
              <p>Visit our office at Nelson Mandela Rd, Arusha, Tanzania</p>
              <div className="map-wrapper">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2546.229627027278!2d36.73874557497239!3d-3.4237187965508067!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x18371b9ea5030a43%3A0xf48d8858c8458348!2sHouse%20of%20Canvas%20Limited%20Arusha%20%7C%20tent%20Manufacturer%20in%20tanzania!5e1!3m2!1sen!2stz!4v1741351230643!5m2!1sen!2stz" 
                  width="100%" 
                  height="450" 
                  style={{ border: 0 }} 
                  allowFullScreen="" 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title="House of Canvas Location"
                ></iframe>
              </div>
              <div className="compass-icon-container">
                <CompassIcon />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Social Media Feed Section */}
      <SocialMediaFeeds />
      
      {/* Google Reviews Section */}
      <GoogleReviews />
      
      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <motion.div 
            className="faq-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>Frequently Asked Questions</h2>
            <p>Find answers to common questions about our services and products</p>
          </motion.div>
          
          <div className="faq-grid">
            <motion.div 
              className="faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3>What types of tents do you manufacture?</h3>
              <p>We manufacture a wide range of tents including stretch tents, frame tents, safari luxury tents, military tents, and custom designs for special events and purposes.</p>
            </motion.div>
            
            <motion.div 
              className="faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3>What is the lead time for custom tent orders?</h3>
              <p>Lead times vary depending on the complexity and size of the order. Typically, our custom tent orders take 2-4 weeks to complete. Please contact us for a more accurate timeline for your specific requirements.</p>
            </motion.div>
            
            <motion.div 
              className="faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h3>Do you offer tent rental services?</h3>
              <p>Yes, we offer tent rental services for events of all sizes. Our rental service includes professional installation, maintenance, and dismantling.</p>
            </motion.div>
            
            <motion.div 
              className="faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <h3>What materials do you use for your tents?</h3>
              <p>We use high-quality, durable materials for our tents including PVC, polyester with PU coating, canvas, and other weather-resistant fabrics. All our materials are selected for their durability, weather resistance, and longevity.</p>
            </motion.div>
            
            <motion.div 
              className="faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <h3>Where are you located?</h3>
              <p>Our main office is located at Nelson Mandela Road, Arusha, Tanzania. We also have operations in Kenya to serve the wider East African market.</p>
            </motion.div>
            
            <motion.div 
              className="faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
            >
              <h3>Can you customize tents with branding?</h3>
              <p>Absolutely! We offer customization services including branding, logos, and custom colors. Our design team will work with you to incorporate your branding elements into the tent design.</p>
            </motion.div>
          </div>
          <motion.div 
            className="faq-more-link"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            viewport={{ once: true }}
          >
            <p>Have more questions? Check out our <Link to="/faq">complete FAQ page</Link> for more information.</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact; 